import { WorkspaceRoleEnum } from '@schemas/workspace'
import {
  BookOpen,
  History,
  LayoutDashboard,
  Library,
  type LucideIcon,
  Megaphone,
  ScrollText,
  Send,
  Settings,
  Users
} from 'lucide-react'
import { useCallback, useEffect } from 'react'
import { NavLink } from 'react-router'
import { getProposals, type SessionResponse } from '../api.ts'
import { canManage } from '../lib/can-manage.ts'
import { cn } from '../lib/cn.ts'
import { AccountMenu } from './account-menu.tsx'
import { useSectionQuery } from './command-center/use-section-query.ts'
import { LOADER_SIZE } from './loading/emission.ts'
import { EmissionMark } from './loading/rally-loader.tsx'
import { RallyMark } from './rally-mark.tsx'

export interface NavProps {
  session: SessionResponse
  /** What the shell is doing while a navigation or submission is in flight past its gate
   * (RAL-182/RAL-188), or null when idle. */
  inFlightLabel?: string | null
}

// RAL-120 composition decision #1 (locked in RAL-118): "Nav is five operational views + Account
// below a rule." Replaces the prior ad hoc set (Review queue / Performance placeholder / Campaign
// setup) — Review lives inside Queue's own review panel, Performance has no v3 view, and
// Campaign setup's live-edit surface is Account (/settings) now that it's a first-class nav item.
//
// RAL-157 adds Campaign as a sixth operational view. RAL-121 gates it on the real role that now
// exists: it is admin-only, and destinationsFor() below owns which set each role sees.
//
// RAL-174 grows the below-the-rule section from one item (Account) to two (Account, Changelog) —
// explicitly requested despite the lock above, since it only touches the utility section, not the
// six operational views.
interface Destination {
  to: string
  label: string
  icon: LucideIcon
}

const QUEUE: Destination = { to: '/queue', label: 'Queue', icon: LayoutDashboard }
const LIBRARY: Destination = { to: '/assets', label: 'Library', icon: Library }
const PUBLISHED: Destination = { to: '/published', label: 'Published', icon: Send }
const INTELLIGENCE: Destination = { to: '/intelligence', label: 'What Rally reads', icon: BookOpen }
const ACTIVITY: Destination = { to: '/activity', label: 'Activity', icon: History }
const CAMPAIGNS: Destination = { to: '/campaigns', label: 'Campaigns', icon: Megaphone }
const TEAM: Destination = { to: '/team', label: 'Team', icon: Users }

// RAL-121 rule 3: restricted views are absent, not disabled — a nav full of locks a member can
// never open is a paywall pretending to be a product. A member's shell drops Activity and
// Campaigns entirely and leads with Published, the view their role is actually for; Team takes
// their fifth slot. The admin baseline is conditional on rule 12: six operational views outside a
// team, seven inside one, because Team only exists once there is a team.
const destinationsFor = ({ role, isTeam }: SessionResponse['activeWorkspace']): Destination[] => {
  if (role === WorkspaceRoleEnum.enum.member) return [PUBLISHED, INTELLIGENCE, QUEUE, LIBRARY, TEAM]
  const admin = [QUEUE, LIBRARY, PUBLISHED, INTELLIGENCE, ACTIVITY, CAMPAIGNS]
  return isTeam ? admin.concat(TEAM) : admin
}

const navItemClass = ({ isActive, isDimmed }: { isActive: boolean; isDimmed: boolean }): string =>
  cn(
    'flex items-center gap-2.5 whitespace-nowrap border-l-[3px] px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.13em] transition-colors duration-150',
    isActive
      ? 'border-l-signal-oxide bg-ground-hover text-ink-primary'
      : isDimmed
        ? 'border-l-transparent text-ink-secondary'
        : 'border-l-transparent text-ink-secondary hover:bg-ground-hover hover:text-ink-primary'
  )

const navIconClass = ({ isActive, isDimmed }: { isActive: boolean; isDimmed: boolean }): string =>
  cn('size-3.5 shrink-0', isDimmed && !isActive && 'text-signal-neutral')

// Removing the blanket modulepreload of every route chunk (RAL-139) made first-time
// navigation to any nav destination pay a cold chunk fetch it previously got for free —
// noticeable as a 1-3s stall on the tab bar. Warm the destination's lazy chunk (component
// + data loader, same specifiers router.tsx passes to lazy()/import()) on hover/focus, so
// the fetch overlaps the user's mouse-to-click dwell time instead of starting at click.
const ROUTE_PREFETCH: Record<string, () => void> = {
  '/queue': () => void import('../routes/home.tsx'),
  '/assets': () => {
    void import('../routes/assets/_layout.tsx')
    void import('../routes/assets/_layout.data.ts')
  },
  '/published': () => {
    void import('../routes/published.tsx')
    void import('../routes/published.data.ts')
  },
  '/intelligence': () => void import('../routes/intelligence.tsx'),
  '/activity': () => {
    void import('../routes/activity.tsx')
    void import('../routes/activity.data.ts')
  },
  '/campaigns': () => {
    void import('../routes/campaigns.tsx')
    void import('../routes/campaigns.data.ts')
  },
  '/settings': () => {
    void import('../routes/settings.tsx')
    void import('../routes/settings.data.ts')
  },
  '/changelog': () => {
    void import('../routes/changelog.tsx')
    void import('../routes/changelog.data.ts')
  },
  '/team': () => {
    void import('../routes/team.tsx')
    void import('../routes/team.data.ts')
  }
}

const HELD_PROPOSALS_LIMIT = 20

// Nav never remounts across navigation (see RootLayout in _root.tsx), so QueueNoticeCountLive's
// held/system_error fetch only ever runs once at load. Without this, approving/rejecting/snoozing
// a proposal on the Queue page updates that page's own list but leaves the sidebar badge showing
// the stale pre-mutation count until a full reload. home.tsx's onMutated dispatches this alongside
// its own reviewQuery.retry() so both stay in sync.
const QUEUE_MUTATED_EVENT = 'rally:queue-mutated'
export const notifyQueueMutated = (): void => {
  document.dispatchEvent(new Event(QUEUE_MUTATED_EVENT))
}

// RAL-118 composition decision: "the sidebar counts are a notice channel" — Queue's count is
// oxide only when something needs the creator, neutral otherwise (including while paused, per
// invariant 5), and "––" while loading or on error rather than a bare 0. Gated the same way as
// AutomationControl: mounted only once there's a real campaign to ask about, so an unauthenticated
// or mid-setup session never fires the request just to throw the result away.
function QueueNoticeCount({ session }: { session: SessionResponse }) {
  if (!session.connected || !session.setupComplete) return null
  return <QueueNoticeCountLive needsYou={canManage(session)} />
}

// A member can't act on a held proposal, so the count is information, not a notice: never oxide (RAL-121 invariant 7).
function QueueNoticeCountLive({ needsYou }: { needsYou: boolean }) {
  const query = useSectionQuery(
    useCallback(async () => {
      const [held, failures] = await Promise.all([
        getProposals(`/api/proposals?status=held&limit=${HELD_PROPOSALS_LIMIT}`),
        getProposals(`/api/proposals?status=system_error&limit=${HELD_PROPOSALS_LIMIT}`)
      ])
      return { proposals: held.proposals.concat(failures.proposals) }
    }, []),
    'queue notice count'
  )

  useEffect(() => {
    document.addEventListener(QUEUE_MUTATED_EVENT, query.retry)
    return () => document.removeEventListener(QUEUE_MUTATED_EVENT, query.retry)
  }, [query.retry])

  if (query.isLoading || query.error) {
    return <span className="ml-auto font-mono text-[9px] text-ink-secondary">––</span>
  }

  const count = query.data?.proposals.length ?? 0
  if (count === 0) return null

  return (
    <span
      className={cn(
        'ml-auto flex size-4 shrink-0 items-center justify-center font-mono text-[9px] font-bold',
        needsYou ? 'bg-signal-oxide text-ink-primary' : 'border border-border-hairline-strong text-ink-secondary'
      )}
    >
      {count}
    </span>
  )
}

// Reads connection/setup state via the `session` prop rather than calling
// useLoaderData() itself. Nav is a plain component rendered inside the root route's
// element (routes/_root.tsx), so useLoaderData() there would already resolve to the
// root loader's SessionResponse, but threading it down as a prop keeps Nav a pure
// function of its props.
//
// Layout: at lg+ this is the reference's fixed 232px left sidebar (sticky, full
// height, right hairline). Below lg it compacts into a top bar where the same
// labelled links wrap onto extra rows, so no destination or state token is lost
// and nothing scrolls horizontally on narrow screens.
export function Nav({ session, inFlightLabel = null }: NavProps) {
  // order-first keeps Nav visually first (left on desktop, top on mobile)
  // despite <main> now preceding it in the DOM — see _root.tsx for why the
  // DOM order changed (keyboard tab order, RAL-32). order-first (order:
  // -9999) is unambiguous with exactly two flex/grid children. If a third
  // top-level sibling is ever added to _root.tsx's shell (a banner, a toast
  // region), give it an explicit order too — an un-ordered default (order: 0)
  // would land between Nav and <main>, not necessarily where intended.
  const dimmed = !session.setupComplete
  const destinations = destinationsFor(session.activeWorkspace)
  return (
    <aside className="order-first flex flex-row items-center justify-between gap-4 border-b border-border-hairline bg-ground-recessed px-4 py-3 lg:sticky lg:top-0 lg:h-dvh lg:w-[232px] lg:shrink-0 lg:flex-col lg:items-stretch lg:justify-between lg:gap-0 lg:overflow-y-auto lg:border-b-0 lg:border-r lg:px-0 lg:py-6">
      <div className="flex min-w-0 flex-1 flex-row items-center gap-4 lg:flex-none lg:flex-col lg:items-stretch lg:gap-8">
        <div className="flex shrink-0 items-baseline gap-2 lg:flex-col lg:items-start lg:gap-2 lg:px-[22px]">
          <span className="flex items-center gap-2.5 text-ink-primary">
            {/* RAL-192: the brand mark itself carries the in-flight signal (same emission loop
                as RallyLoader) instead of a second mark rendering redundantly below it. sm's
                opacity channel is deliberate at this height — see emission.ts's note on scaleY
                jitter below ~40px — so the geometry stays frozen here too. */}
            {inFlightLabel ? <EmissionMark size="sm" ink="var(--color-ink-primary)" height={22} /> : <RallyMark />}
            <span className="font-display text-xl font-extrabold uppercase leading-[0.9] tracking-tight [font-stretch:120%] lg:text-[28px]">
              Rally
            </span>
          </span>
          {/* The readout slot: while a navigation or submission is in flight the label replaces
              the build line for the duration. min-h holds the taller of the two so the sidebar
              never shifts. */}
          <span className="hidden min-h-5 items-center font-mono text-[9px] uppercase tracking-[0.18em] text-ink-secondary lg:flex">
            {inFlightLabel ? (
              <span
                className={cn('font-mono uppercase tracking-[0.13em]', LOADER_SIZE.sm.labelClass)}
                style={{ color: 'var(--color-ink-meta)' }}
              >
                {inFlightLabel}
              </span>
            ) : (
              'Creator_Ops · v0.4'
            )}
          </span>
        </div>

        <nav
          aria-label="Primary"
          className="flex min-w-0 flex-row flex-wrap items-center gap-1 lg:flex-col lg:items-stretch lg:gap-0"
        >
          {destinations.map(({ to, label, icon: Icon }) => {
            // Queue is always reachable at full brightness — it's the landing destination and
            // owns the "finish setup" gate itself (home.tsx), so it was never dimmed pre-setup.
            const itemDimmed = to === QUEUE.to ? false : dimmed
            return (
              <NavLink
                key={to}
                to={to}
                onMouseEnter={() => ROUTE_PREFETCH[to]?.()}
                onFocus={() => ROUTE_PREFETCH[to]?.()}
                className={({ isActive }) =>
                  cn(
                    navItemClass({ isActive, isDimmed: itemDimmed }),
                    // Board C puts Team in its own band under a rule: it is about the workspace,
                    // not the campaign, so it doesn't sit in the same run as the operational views.
                    to === TEAM.to && 'mt-1.5 border-t border-border-divider pt-4'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon aria-hidden className={navIconClass({ isActive, isDimmed: itemDimmed })} />
                    <span>{label}</span>
                    {to === QUEUE.to ? <QueueNoticeCount session={session} /> : null}
                  </>
                )}
              </NavLink>
            )
          })}

          <div aria-hidden="true" className="my-2 border-t border-border-hairline" />

          <NavLink
            to="/settings"
            onMouseEnter={() => ROUTE_PREFETCH['/settings']?.()}
            onFocus={() => ROUTE_PREFETCH['/settings']?.()}
            className={({ isActive }) => navItemClass({ isActive, isDimmed: false })}
          >
            {({ isActive }) => (
              <>
                <Settings aria-hidden className={navIconClass({ isActive, isDimmed: false })} />
                <span>Account</span>
              </>
            )}
          </NavLink>

          {/* RAL-174: below-the-rule utility section grows to two items. Always full brightness,
              like Account — this is product-level info, not gated by campaign/setup state. */}
          <NavLink
            to="/changelog"
            onMouseEnter={() => ROUTE_PREFETCH['/changelog']?.()}
            onFocus={() => ROUTE_PREFETCH['/changelog']?.()}
            className={({ isActive }) => navItemClass({ isActive, isDimmed: false })}
          >
            {({ isActive }) => (
              <>
                <ScrollText aria-hidden className={navIconClass({ isActive, isDimmed: false })} />
                <span>Changelog</span>
              </>
            )}
          </NavLink>
        </nav>

        <div className="hidden lg:block lg:px-[22px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-ink-secondary">Channels</p>
          {session.connected ? (
            <div className="mt-2.5 flex items-center gap-2 text-xs text-ink-soft">
              <span aria-hidden className="size-1.5 shrink-0 bg-signal-ok" />
              {/* The picker itself lives in the shell header now (RAL-121 board E) — this stays a
                  readout so the sidebar doesn't carry a second copy of the same control. */}
              <span className="truncate">{session.displayName ?? 'Channel linked'}</span>
            </div>
          ) : (
            <div className="mt-2.5 border border-border-hairline-strong bg-ground-surface px-2.5 py-2">
              <p className="flex items-center gap-2 text-xs text-ink-secondary">
                <span aria-hidden className="size-1.5 shrink-0 border border-border-hairline-strong" />
                No channel connected
              </p>
              <p className="mt-1 pl-3.5 font-mono text-[9px] text-ink-secondary">
                Rally is read-only until one is linked.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 lg:block lg:border-t lg:border-border-hairline lg:px-[22px] lg:pt-4">
        <AccountMenu session={session} />
      </div>
    </aside>
  )
}
